"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
require("core-js/modules/web.dom-collections.iterator.js");
var _react = _interopRequireDefault(require("react"));
var _constants = require("../../utils/constants");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * @module useWindowSize
 */

/**
 * Custom hook to determine and return `window` dimensions and calculate and set
 * various boolean breakpoints for `isDesktop`, `isMobile`, and `isTablet`, as
 * well as an object of `breakpoints` sizes.
 *
 * If executed  in an environment with no window object, numeric values are `0`
 * and all boolean-based values will be `false`.
 *
 * The various attributes of the returned state object include and are
 * calculated as follows:
 * - breakpoints: `{ large: 1024, medium: 768, small: 640, xLarge: 1200 }`.
 * - height: `window.innerHeight`.
 * - isDesktop: `window.innerWidth >= 1024`.
 * - isMobile: `window.innerWidth < 640`.
 * - isTablet: `window.innerWidth >= 640 && window.innerWidth < 1024`.
 * - width: `window.innerWidth`.
 *
 * @example
 * // Usage
 * import React from 'react';
 * import useWindowSize from '../path/to/hooks/useWindowSize';
 *
 * function MyComponent() {
 *   const { isMobile } = useWindowSize();
 *
 *   return (
 *     <div
 *       className={`container px-normal ${
 *         isMobile ? sbOnMobile : sbOnTabletAndUp
 *       }`}
 *       id={componentIdProp}
 *     >
 *       <div>...</div>
 *     </div>
 *   );
 * }
 *
 * @returns {object} Object with window dimension data.
 *
 * @see Hook: useWindowSize {@link https://usehooks.com/useWindowSize/}.
 * @see Perils of Rehydration {@link https://joshwcomeau.com/react/the-perils-of-rehydration/}.
 */
function useWindowSize() {
  const [windowSize, setWindowSize] = _react.default.useState({
    breakpoints: _constants.BREAKPOINTS,
    height: window && window.innerHeight ? window.innerHeight : 0,
    isDesktop: window && window.innerWidth ? window.innerWidth >= _constants.BREAKPOINTS.large : false,
    isMobile: window && window.innerWidth ? window.innerWidth < _constants.BREAKPOINTS.small : true,
    isTablet: window && window.innerWidth && window.innerWidth >= _constants.BREAKPOINTS.small && window && window.innerWidth && window.innerWidth < _constants.BREAKPOINTS.large,
    width: window && window.innerWidth ? window.innerWidth : 0
  });

  /**
   * Single-run effect to add and listen for window resize, and set state.
   */
  _react.default.useEffect(() => {
    /**
     * Handler function for resize event.
     */
    function handleResize() {
      setWindowSize({
        breakpoints: _constants.BREAKPOINTS,
        height: window.innerHeight,
        isDesktop: window.innerWidth >= _constants.BREAKPOINTS.large,
        isMobile: window.innerWidth < _constants.BREAKPOINTS.small,
        isTablet: window.innerWidth >= _constants.BREAKPOINTS.small && window.innerWidth < _constants.BREAKPOINTS.large,
        width: window.innerWidth
      });
    }
    // Add event listener for handling resize events, call right away so the
    // state is updated with initial size values, then remove for cleanup.
    // Only add when there is a legitimate window object.
    if (window && window.addEventListener && typeof window.addEventListener === 'function') {
      window.addEventListener('resize', handleResize);
      handleResize();
      return () => window.removeEventListener('resize', handleResize);
    }
    /* istanbul ignore next */
    return () => {};
  }, []);
  return windowSize;
}
var _default = exports.default = useWindowSize;